// client/src/components/LobbySettingsModal.jsx
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import '../style/LobbySettingsModal.css';

function LobbySettingsModal({ onClose, settings, setSettings}) {
    const {
        mode,
        numberOfQuestions,
        selectedCategories,
        selectedSubCategories,
        difficulty,
        avoidUsedQuestions,
        duration
    } = settings;

    const durationSec = Math.round((duration || 15000) / 1000);
    const [categories, setCategories] = useState([]);
    const [availableSubCategories, setAvailableSubCategories] = useState([]);
    const [imageCategories, setImageCategories] = useState(['League of Legends']);
    const imageDifficultyOptions = ['Schwarz/Weiß', 'Verpixelt', 'Ausschnitt'];

    const difficultyOptions = ['einfach','mittel','schwer'];
    const modeOptions = ['quiz','image'];
    const [availableCount, setAvailableCount] = useState(null);
    const [questionCountError, setQuestionCountError] = useState('');
    const modalContentRef = useRef(null);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axios.get(`/api/categories`);
                setCategories(res.data);
            } catch (err) {
                console.error('Fehler beim Laden der Kategorien', err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
                handleSave();
            }
        };

        // Event-Listener hinzufügen, wenn das Modal sichtbar ist
        document.addEventListener('mousedown', handleClickOutside);

        // Event-Listener beim Unmounten der Komponente oder wenn sich onClose/handleSave ändert, wieder entfernen
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleSave();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    useEffect(() => {
        const selectedSubSets = categories
            .filter(cat => selectedCategories.includes(cat.kategorie))
            .flatMap(cat => cat.subKategorien || []);

        setAvailableSubCategories([...new Set(selectedSubSets)]); // doppelte SubKategorien entfernen
    }, [selectedCategories, categories]);

    const fetchAvailable = async () => {
        try {
            const res = await axios.post('/api/questions/available', {
                selectedCategories,
                selectedSubCategories,
                difficulty,
                avoidUsedQuestions,
                lobbyCode: window.location.pathname.split('/').pop() // oder anders
            });
            setAvailableCount(res.data.available);
        } catch (err) {
            console.error('Fehler beim Zählen der verfügbaren Fragen', err);
        }
    };

    useEffect(() => {
        fetchAvailable();
    }, [selectedCategories, selectedSubCategories, difficulty, avoidUsedQuestions]);

    const update = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const toggleDifficulty = lvl => {
        const arr = difficulty.includes(lvl)
            ? difficulty.filter(d => d !== lvl)
            : [...difficulty, lvl];
        update('difficulty', arr);
    };

    const toggleCategory = category => {
        if (selectedCategories.includes(category)) {
            // Entferne Kategorie
            const newCategories = selectedCategories.filter(c => c !== category);
            // Entferne auch alle Sub-Kategorien, die zu dieser Kategorie gehören
            const catObj = categories.find(c => c.kategorie === category);
            const subCatsToRemove = catObj?.subKategorien || [];
            const newSubCategories = selectedSubCategories.filter(
                sc => !subCatsToRemove.includes(sc)
            );

            update('selectedCategories', newCategories);
            update('selectedSubCategories', newSubCategories);
        } else {
            // Füge Kategorie hinzu
            update('selectedCategories', [...selectedCategories, category]);
        }
    };

    const toggleSubCategory = subCategory => {
        if (selectedSubCategories.includes(subCategory)) {
            update('selectedSubCategories',
                selectedSubCategories.filter(sc => sc !== subCategory)
            );
        } else {
            update('selectedSubCategories', [...selectedSubCategories, subCategory]);
        }
    };

    const toggleImageCategory = category => {
        const currentCategories = settings.imageCategories || [];
        if (currentCategories.includes(category)) {
            update('imageCategories', currentCategories.filter(c => c !== category));
        } else {
            update('imageCategories', [...currentCategories, category]);
        }
    };

    const toggleImageDifficulty = diff => {
        const currentDifficulties = settings.imageDifficulties || [];
        if (currentDifficulties.includes(diff)) {
            update('imageDifficulties', currentDifficulties.filter(d => d !== diff));
        } else {
            update('imageDifficulties', [...currentDifficulties, diff]);
        }
    };

    const handleSave = () => {
        // Parse den aktuellen Wert nochmals, falls der User etwas tippt und nicht onBlur auslöst
        let currentNumberOfQuestions = settings.numberOfQuestions;
        if (typeof currentNumberOfQuestions !== 'number' || isNaN(currentNumberOfQuestions)) {
            currentNumberOfQuestions = parseInt(String(currentNumberOfQuestions), 10) || 0;
        }


        if (currentNumberOfQuestions <= 0) {
            setQuestionCountError('Anzahl der Fragen kann nicht 0 oder weniger betragen.');
            return; // Verhindere das Schließen/Speichern
        }
        setQuestionCountError(''); // Fehler zurücksetzen
        onClose(settings); // Rufe onClose auf, WENN es auch die Settings speichert.
    };

    return (
        <div className="modal-lobby-settings-backdrop">
            <div className="modal-lobby-settings-content">
                <div className="modal-lobby-settings-inner" ref={modalContentRef}>
                    <h2>Lobby-Einstellungen</h2>
                    <button className="modal-lobby-settings-close" onClick={handleSave}>×</button>

                    {/* Modus */}
                    <div className="settings-group">
                        <label>Modus:</label>
                        <div className="mode-options">
                            {modeOptions.map(m => (
                                <div
                                    key={m}
                                    className={`mode-option ${mode === m ? 'selected' : ''}`}
                                    onClick={() => update('mode', m)}
                                >
                                    {m.charAt(0).toUpperCase() + m.slice(1)}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Quiz-Spezifische Einstellungen */}
                    {mode === 'quiz' && (
                        <>
                            {/* Kategorien */}
                            <div className="settings-group">
                                <label>Kategorien auswählen:</label>
                                <div className="tag-container">
                                    {categories.map(cat => (
                                        <div
                                            key={cat.kategorie}
                                            className={`tag ${selectedCategories.includes(cat.kategorie) ? 'selected' : ''}`}
                                            onClick={() => toggleCategory(cat.kategorie)}
                                        >
                                            {cat.kategorie}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Sub-Kategorien */}
                            {availableSubCategories.length > 0 && (
                                <div className="settings-group">
                                    <label>Sub-Kategorien auswählen:</label>
                                    <div className="tag-container">
                                        {availableSubCategories.map(sub => (
                                            <div
                                                key={sub}
                                                className={`tag ${selectedSubCategories.includes(sub) ? 'selected' : ''}`}
                                                onClick={() => toggleSubCategory(sub)}
                                            >
                                                {sub}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Anzahl der Fragen */}
                            <div className="settings-group">
                                <label htmlFor="numberOfQuestionsInput">Anzahl der Fragen:</label> {/* htmlFor für Accessibility */}
                                <input
                                    id="numberOfQuestionsInput" // Passende ID für das Label
                                    type="number"
                                    value={settings.numberOfQuestions === 0 || isNaN(settings.numberOfQuestions) ? "" : settings.numberOfQuestions}
                                    onFocus={(e) => e.target.select()}
                                    onChange={e => {
                                        const rawValue = e.target.value;
                                        let numValue;
                                        if (rawValue === "") {
                                            update('numberOfQuestions', 0);
                                        } else {
                                            numValue = parseInt(rawValue, 10);
                                        }

                                        if (!isNaN(numValue)) {
                                            update('numberOfQuestions', numValue);
                                        } else if (rawValue === "" || rawValue === "-") {
                                            // Erlaube dem Nutzer, das Feld temporär zu leeren oder ein Minus zu tippen
                                            // Der State wird dann zu 0 oder einer negativen Zahl (die beim Speichern abgefangen wird)
                                            update('numberOfQuestions', rawValue === "" ? 0 : NaN); // NaN damit der Input leer bleibt, 0 wenn es 0 sein soll
                                        }
                                        // Wenn die Eingabe komplett ungültig ist (z.B. "abc"), wird der State nicht geändert,
                                        // das Input-Feld behält dank React den letzten gültigen State-Wert.
                                    }}
                                />
                                {questionCountError && <p className="error-message">{questionCountError}</p>}
                            </div>
                            {/* Dauer via Slider */}
                            <div className="settings-group">
                                <label>
                                    Spieldauer: {durationSec} Sek
                                </label>
                                <input
                                    type="range"
                                    min="5"
                                    max="60"
                                    step="1"
                                    value={durationSec}
                                    onChange={e => update('duration', parseInt(e.target.value, 10) * 1000)}
                                />
                            </div>

                            {/* Verlauf-Einstellungen */}
                            <div className="settings-group">
                                <div className="history-settings-header">
                                    <i className="fas fa-brain"></i>
                                    <span>Fragen-Verlauf</span>
                                </div>
                                <div className="history-options-group">
                                    <div className="history-option">
                                        <label className="checkbox-container">
                                            <input
                                                type="checkbox"
                                                checked={avoidUsedQuestions || false}
                                                onChange={(e) => update('avoidUsedQuestions', e.target.checked)}
                                            />
                                            <span className="checkmark"></span>
                                            <span className="checkbox-label">Bereits verwendete Fragen vermeiden</span>
                                        </label>

                                        {availableCount !== null && (
                                            <div className="available-counter">
                                                <div className="counter-info">
                                                    <i className="fas fa-database counter-icon"></i>
                                                    <span className="counter-text">Verfügbare Fragen: <strong>{availableCount}</strong></span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        className="button button-danger history-reset"
                                        onClick={async () => {
                                            const resetNotification = document.getElementById('reset-notification');
                                            try {
                                                const confirmReset = window.confirm('Möchtest du wirklich die Spiel-Historie löschen?');
                                                if (!confirmReset) return;
                                                const lobbyCode = window.location.pathname.split('/').pop();
                                                await axios.post(`/api/lobby/${lobbyCode}/reset-history`);
                                                if (resetNotification) {
                                                    resetNotification.innerText = 'Spielhistorie erfolgreich zurückgesetzt';
                                                    resetNotification.className = 'reset-notification success';
                                                    resetNotification.style.display = 'block';
                                                    setTimeout(() => {
                                                        resetNotification.style.display = 'none';
                                                    }, 3000);
                                                }
                                                fetchAvailable();
                                            } catch (err) {
                                                console.error('Fehler beim Zurücksetzen:', err);
                                                if (resetNotification) {
                                                    resetNotification.innerText = 'Fehler beim Zurücksetzen der Historie';
                                                    resetNotification.className = 'reset-notification error';
                                                    resetNotification.style.display = 'block';
                                                    setTimeout(() => {
                                                        resetNotification.style.display = 'none';
                                                    }, 3000);
                                                }
                                            }
                                        }}
                                    >
                                        <i className="fas fa-history reset-icon"></i> Spielhistorie löschen
                                    </button>
                                    <div id="reset-notification" className="reset-notification" style={{display: 'none'}}></div>
                                </div>
                            </div>

                            {/* Difficulty */}
                            <div className="settings-group">
                                <label>Schwierigkeit:</label>
                                <div className="difficulty-select">
                                    {difficultyOptions.map((level) => {
                                        const isSelected = difficulty.includes(level);
                                        return (
                                            <div
                                                key={level}
                                                className={`difficulty-box ${isSelected ? 'selected' : ''}`}
                                                onClick={() => toggleDifficulty(level)}
                                            >
                                                {level.charAt(0).toUpperCase() + level.slice(1)}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Image-Spezifische Einstellungen */}
                    {mode === 'image' && (
                        <>
                            {/* Bild-Kategorien */}
                            <div className="settings-group">
                                <div className="settings-header">
                                    <i className="fas fa-image"></i>
                                    <span>Bild-Kategorien</span>
                                </div>
                                <div className="tag-container">
                                    {imageCategories.map(category => (
                                        <div
                                            key={category}
                                            className={`tag ${(settings.imageCategories || []).includes(category) ? 'selected' : ''}`}
                                            onClick={() => toggleImageCategory(category)}
                                        >
                                            {category}
                                        </div>
                                    ))}
                                </div>
                                {settings.imageDifficulties?.includes('Verpixelt') && (
                                    <div className="settings-group">
                                        <label>Verpixelungsstärke:</label>
                                        <div className="tag-container">
                                            {['Einfach', 'Mittel', 'Schwer', 'Alptraum'].map(opt => {
                                                const strengthMap = { 'Einfach': 0.85,'Mittel': 0.90, 'Schwer': 0.95, 'Alptraum': 0.97 };
                                                return (
                                                    <div
                                                        key={opt}
                                                        className={`tag ${settings.pixelationStrength === strengthMap[opt] ? 'selected' : ''}`}
                                                        onClick={() => update('pixelationStrength', strengthMap[opt])}
                                                    >
                                                        {opt}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Anzahl der Fragen */}
                            <div className="settings-group">
                                <label htmlFor="numberOfQuestionsInput">Anzahl der Fragen:</label> {/* htmlFor für Accessibility */}
                                <input
                                    id="numberOfQuestionsInput" // Passende ID für das Label
                                    type="number"
                                    value={settings.numberOfQuestions} // Direkt an den State gebunden
                                    onFocus={(e) => e.target.select()}
                                    onChange={e => {
                                        const rawValue = e.target.value;
                                        let numValue;
                                        if (rawValue === "") {
                                            numValue = 0; // Behandle leeres Feld als 0 für den State
                                        } else {
                                            numValue = parseInt(rawValue, 10);
                                        }

                                        if (!isNaN(numValue)) {
                                            update('numberOfQuestions', numValue);
                                        } else if (rawValue === "" || rawValue === "-") {
                                            update('numberOfQuestions', rawValue === "" ? 0 : NaN); // NaN damit der Input leer bleibt, 0 wenn es 0 sein soll
                                        }
                                    }}
                                />
                                {questionCountError && <p className="error-message">{questionCountError}</p>}
                            </div>
                            {/* Dauer via Slider */}
                            <div className="settings-group">
                                <label>
                                    Spieldauer: {durationSec} Sek
                                </label>
                                <input
                                    type="range"
                                    min="5"
                                    max="30"
                                    step="1"
                                    value={durationSec}
                                    onChange={e => update('duration', parseInt(e.target.value, 10) * 1000)}
                                />
                            </div>

                            {/* Bild-Schwierigkeit */}
                            <div className="settings-group">
                                <div className="settings-header">
                                    <i className="fas fa-sort-amount-up"></i>
                                    <span>Bild-Schwierigkeit</span>
                                </div>
                                <div className="difficulty-select">
                                    {imageDifficultyOptions.map((diff) => (
                                        <div
                                            key={diff}
                                            className={`difficulty-box ${(settings.imageDifficulties || []).includes(diff) ? 'selected' : ''}`}
                                            onClick={() => toggleImageDifficulty(diff)}
                                        >
                                            {diff}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="modal-lobby-settings-buttons">
                    <button className="button button-primary" onClick={handleSave}>Speichern</button>
                </div>
            </div>
        </div>
    );
}

export default LobbySettingsModal;
